import type { ReactElement } from "react";

/**
 * Modern, minimal brand glyphs (single-colour, `currentColor`) designed to sit
 * inside the gradient app-icon mark. Stored per-tenant as `theme.icon` (a key).
 */

const GLYPHS: Record<string, ReactElement> = {
  spark: (
    <path
      fill="currentColor"
      stroke="none"
      d="M12 2c.6 4.7 3.7 7.8 8 8-4.3.2-7.4 3.3-8 8-.6-4.7-3.7-7.8-8-8 4.3-.2 7.4-3.3 8-8Z"
    />
  ),
  hexa: (
    <>
      <path d="M12 2.6 20.4 7v10L12 21.4 3.6 17V7L12 2.6Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  cube: (
    <>
      <path d="M12 2.6 20.4 7v10L12 21.4 3.6 17V7L12 2.6Z" />
      <path d="M12 21.4V12M12 12 3.6 7M12 12l8.4-5" />
    </>
  ),
  orbit: (
    <>
      <circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none" />
      <ellipse cx="12" cy="12" rx="9.5" ry="4.3" transform="rotate(-28 12 12)" />
    </>
  ),
  prism: (
    <>
      <path d="M12 3 21 19H3L12 3Z" />
      <path d="M12 3v16" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3 3 8l9 5 9-5-9-5Z" />
      <path d="M3 13l9 5 9-5" />
    </>
  ),
  bolt: <path fill="currentColor" stroke="none" d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />,
  wave: <path d="M2 14c3 0 3-6 6-6s3 6 6 6 3-6 6-6" />,
  grid: (
    <>
      <circle cx="8" cy="8" r="2.1" fill="currentColor" stroke="none" />
      <circle cx="16" cy="8" r="2.1" fill="currentColor" stroke="none" />
      <circle cx="8" cy="16" r="2.1" fill="currentColor" stroke="none" />
      <circle cx="16" cy="16" r="2.1" fill="currentColor" stroke="none" />
    </>
  ),
  shield: (
    <>
      <path d="M12 2 20 5v6c0 5-3.5 8.6-8 11-4.5-2.4-8-6-8-11V5l8-3Z" />
      <path d="m8.5 12 2.4 2.4 4.6-4.8" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9.4" />
      <path fill="currentColor" stroke="none" d="m15.6 8.4-2 5.2-5.2 2 2-5.2 5.2-2Z" />
    </>
  ),
  rings: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.4" />
    </>
  ),
  nova: (
    <>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
    </>
  ),
  monogram: (
    <>
      <path d="M5 21 12 4l7 17" />
      <path d="M8.2 14.6h7.6" />
    </>
  ),
};

export const BRAND_ICON_KEYS: string[] = Object.keys(GLYPHS);
export const DEFAULT_BRAND_ICON = "hexa";

export function BrandGlyph({ id, size = 18 }: { id?: string; size?: number }) {
  const key = id && id in GLYPHS ? id : DEFAULT_BRAND_ICON;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPHS[key]}
    </svg>
  );
}
